import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toBlob } from 'html-to-image';
import { getSignBySlug, ZODIAC_SIGNS, signImageUrl } from '../data/zodiacSigns';
import { compatibilityService, CompatibilityCardData } from '../services/compatibilityApi';
import CompatibilityCard, { CARD_W, CARD_H } from '../components/CompatibilityCard';
import { useAuth } from '../hooks/useAuth';

const CompatibilidadePage: React.FC = () => {
  const { signA: slugA, signB: slugB } = useParams<{ signA: string; signB: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cardRef = useRef<HTMLDivElement>(null);
  const previewWrapRef = useRef<HTMLDivElement>(null);

  const signA = getSignBySlug(slugA) || ZODIAC_SIGNS[0];
  const signB = getSignBySlug(slugB) || ZODIAC_SIGNS[4];

  const [data, setData] = useState<CompatibilityCardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Escala do preview: o card é nativo em 1080px e reduzido na tela.
  const [scale, setScale] = useState(0.3);
  useLayoutEffect(() => {
    const el = previewWrapRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth || 360;
      setScale(Math.min(w, 440) / CARD_W);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);

  useEffect(() => {
    document.title = `${signA.name} + ${signB.name}: Compatibilidade — Astralyz`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', `Veja a compatibilidade amorosa entre ${signA.name} e ${signB.name}: família, amizade, sexo e amor. Gerado pelo Astralyz.`);
    }
    fetchData();
    return () => { document.title = 'Astralyz — Tarot & Astrologia'; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signA.slug, signB.slug]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    setDownloadError(null);
    try {
      const result = await compatibilityService.getCompatibility(signA.name, signB.name);
      setData(result);
    } catch {
      setError('Não foi possível carregar a compatibilidade agora.');
    } finally {
      setLoading(false);
    }
  };

  /** Gera o PNG do card em 1080×1920 como Blob. */
  const generateBlob = useCallback(async (): Promise<Blob> => {
    const node = cardRef.current;
    if (!node) throw new Error('card ainda não renderizado');

    // Garante que as imagens dos signos estão decodificadas antes da captura —
    // o html-to-image não espera <img> pendentes e sai com buracos.
    await Promise.all(
      [signA, signB].map(s => new Promise<void>(resolve => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = signImageUrl(s);
      }))
    );

    // `skipFonts: true` evita a etapa de embutir web fonts (Playfair vem por
    // @import do Google Fonts) — é onde o html-to-image costumava travar/estourar,
    // principalmente no mobile. O card cai no fallback serif do sistema.
    // Sem `cacheBust` pra não re-baixar as imagens dos signos (já estão em cache).
    // `Promise.race` com timeout pra nunca deixar o botão preso em "Gerando…".
    const capture = toBlob(node, {
      width: CARD_W,
      height: CARD_H,
      pixelRatio: 1,
      skipFonts: true,
      backgroundColor: '#17102a',
    });
    const blob = await Promise.race<Blob | null>([
      capture,
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('timeout na geração da imagem')), 30000)
      ),
    ]);
    if (!blob) throw new Error('toBlob retornou vazio');
    return blob;
  }, [signA, signB]);

  const fileName = `compatibilidade-${signA.slug}-${signB.slug}.png`;

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await generateBlob();
      const url = URL.createObjectURL(blob);

      const downloadViaLink = () => {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
      };

      // Em touch (celular) o menu de compartilhar do sistema é a melhor opção —
      // permite "Salvar imagem" e postar direto no story. No desktop vai direto
      // pro download por link (o share abre um seletor confuso do SO).
      const file = new File([blob], fileName, { type: 'image/png' });
      const isTouch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;

      if (isTouch && navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: `${signA.name} + ${signB.name}`,
            text: `Veja a compatibilidade entre ${signA.name} e ${signB.name} no Astralyz!`,
          });
        } catch (shareErr: any) {
          // usuário cancelou → não faz nada; qualquer outro erro → cai no download
          if (shareErr?.name !== 'AbortError') downloadViaLink();
        }
      } else {
        downloadViaLink();
      }

      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (err: any) {
      console.error('Erro ao gerar imagem:', err);
      setDownloadError('Não foi possível gerar a imagem. Atualize a página e tente de novo.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-mystical">
      {!user && (
        <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="text-white/60 hover:text-white transition-colors text-sm font-mystical tracking-widest uppercase">
              Astralyz
            </Link>
            <Link to="/register" className="text-xs px-4 py-2 rounded-full border border-white/20 text-white/70 hover:border-white/50 hover:text-white transition-all">
              Criar conta grátis
            </Link>
          </div>
        </header>
      )}

      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-mystical font-bold text-white mb-2">
            💞 Compatibilidade entre Signos
          </h1>
          <button
            onClick={() => navigate('/compatibilidade')}
            className="text-white/40 hover:text-white/70 text-sm transition-colors"
          >
            ← Trocar signos
          </button>
        </div>

        {loading && (
          <div className="flex flex-col items-center py-16 gap-4">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              className="text-4xl"
            >
              ✦
            </motion.div>
            <p className="text-white/40 text-sm">Aurora está consultando os astros…</p>
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-12">
            <p className="text-white/50 mb-4">{error}</p>
            <button
              onClick={fetchData}
              className="px-6 py-2 bg-mystical-600 text-white rounded-full text-sm hover:bg-mystical-700 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {data && !loading && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {/* Preview visível: o card é nativo 1080×1920, só reduzido com scale. */}
            <div ref={previewWrapRef} className="w-full flex justify-center">
              <div
                style={{ width: CARD_W * scale, height: CARD_H * scale }}
                className="relative overflow-hidden rounded-2xl"
              >
                <div
                  style={{
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    width: CARD_W,
                    height: CARD_H,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                  }}
                >
                  <CompatibilityCard signA={signA} signB={signB} data={data} />
                </div>
              </div>
            </div>

            {/* Cópia oculta em tamanho real (sem transform de ancestral) —
                é esta que o html-to-image captura, pra sair 1080×1920 exato. */}
            <div aria-hidden style={{ position: 'fixed', left: -99999, top: 0, width: CARD_W, height: CARD_H, pointerEvents: 'none', opacity: 0 }}>
              <CompatibilityCard signA={signA} signB={signB} data={data} cardRef={cardRef} />
            </div>

            <div className="mt-6 flex flex-col items-center gap-2">
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="px-8 py-3 rounded-full text-white font-bold text-sm transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #a78bfa)' }}
              >
                {downloading ? 'Gerando imagem…' : '📲 Baixar / Compartilhar'}
              </button>
              {downloadError && <p className="text-red-400 text-xs text-center max-w-xs">{downloadError}</p>}
            </div>

            <div className="text-center rounded-2xl p-6 sm:p-8 bg-white/5 border border-white/10 mt-8">
              <p className="text-white/50 text-sm mb-2">Com carinho por Aurora Lyz</p>
              <h2 className="text-white font-mystical font-bold text-xl sm:text-2xl mb-3">
                Quer uma compatibilidade completa?
              </h2>
              <p className="text-white/50 text-sm mb-6 max-w-sm mx-auto">
                A Sinastria analisa o mapa astral completo dos dois, não só o Sol.
                {user ? ' Cadastre suas conexões e veja a análise completa.' : ' Crie sua conta e cadastre suas conexões.'}
              </p>
              {user ? (
                <Link
                  to="/profile?tab=conexoes"
                  className="inline-block px-8 py-3 rounded-full text-white font-bold text-base transition-opacity hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #a78bfa)' }}
                >
                  🔮 Ver Sinastria completa
                </Link>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="inline-block px-8 py-3 rounded-full text-white font-bold text-base transition-opacity hover:opacity-90 mb-3"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #a78bfa)' }}
                  >
                    ✨ Criar conta grátis
                  </Link>
                  <p className="text-white/30 text-xs mt-3">
                    Já tem conta?{' '}
                    <Link to="/login" className="text-gold-400 hover:text-gold-300 transition-colors">
                      Entrar
                    </Link>
                  </p>
                </>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CompatibilidadePage;
